/*
 * The master inbox's keyboard, as its help lists it (step 10): moved out of `use-inbox-page.ts` once
 * Settings came to list the same keys, so the two can never disagree. Pure: what the keys do is
 * `useKeys` in that hook; this is only what the panel and Settings say they do.
 */
import type { ShortcutEntry } from "../page-shortcuts.tsx";

/** The modifier the reply editor's link key takes on this computer (`editor-links.tsx`). */
const MOD =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘" : "Ctrl";

/**
 * Every key the inbox answers to, for the shortcuts panel (`?`), on a desktop only: `useKeys` in
 * `use-inbox-page.ts` and, for the fold, `Messages` in `thread.tsx`,
 * the shell's go-to chords, `W` and `?`, the search field's Backspace, the reply sheet's Esc and its editor's link
 * key. In four groups, so the list reads in chunks: moving and finding, the open conversation, the
 * reply, and the selection, where E, H, L and U act on every ticked row.
 */
const MAIL_SHORTCUTS: readonly ShortcutEntry[] = [
  { group: "Move and find", keys: "J / K", does: "Next or previous conversation" },
  { group: "Move and find", keys: "/", does: "Search" },
  { group: "Move and find", keys: "Backspace", does: "Take off the last search filter" },
  { group: "Move and find", keys: "Esc", does: "Close a menu" },
  {
    group: "Move and find",
    keys: "G then I / M / N / F",
    does: "Go to Inbox, Meeting requests, Needs reply or Follow-up due",
  },
  { group: "Move and find", keys: "W", does: "Switch workspace" },
  { group: "Move and find", keys: "?", does: "Show these shortcuts" },
  { group: "The open conversation", keys: "O", does: "Show or hide earlier messages" },
  { group: "The open conversation", keys: "E", does: "Done, or back in the inbox" },
  { group: "The open conversation", keys: "#", does: "Delete, or restore one in Trash" },
  { group: "The open conversation", keys: "H", does: "Snooze, or unsnooze a snoozed one" },
  { group: "The open conversation", keys: "U", does: "Mark seen or unseen" },
  { group: "The open conversation", keys: "L", does: "Relabel" },
  { group: "The open conversation", keys: "1 to 8", does: "Choose a label in the open label menu" },
  { group: "The open conversation", keys: "C", does: "Confirm an unsure label" },
  { group: "The open conversation", keys: "M", does: "Assign to someone" },
  { group: "The open conversation", keys: ".", does: "More: snooze, mark seen, delete" },
  { group: "Your reply", keys: "R", does: "Reply" },
  { group: "Your reply", keys: `${MOD} + K`, does: "Add a link to a formatted reply" },
  { group: "Your reply", keys: `${MOD} + Enter`, does: "Send the reply" },
  { group: "Your reply", keys: "Esc", does: "Fold the reply away; its text is kept" },
  { group: "Selected conversations", keys: "X", does: "Select or unselect the conversation" },
  {
    group: "Selected conversations",
    keys: "Shift + J / K",
    does: "Select as you move; back again unselects; Shift and the arrows too",
  },
  {
    group: "Selected conversations",
    keys: "Shift + click",
    does: "Select every row from the last one",
  },
  { group: "Selected conversations", keys: `${MOD} + click`, does: "Select or unselect one row" },
  {
    group: "Selected conversations",
    keys: `${MOD} + A`,
    does: "Select every conversation on this page, from the list",
  },
  { group: "Selected conversations", keys: "E / H / L", does: "Done, snooze or relabel them" },
  { group: "Selected conversations", keys: "#", does: "Delete them, or restore them in Trash" },
  { group: "Selected conversations", keys: "M", does: "Assign them" },
  { group: "Selected conversations", keys: "Shift + I", does: "Mark them seen" },
  { group: "Selected conversations", keys: "U", does: "Mark them unseen" },
  { group: "Selected conversations", keys: "Esc", does: "Clear the selection" },
  { group: "Selected conversations", keys: "Z", does: "Undo the last action" },
];

/*
 * Where G goes beyond the inbox: Campaigns and Standing answers are the reply agent's, so without it
 * G then S, to Settings, is the only one (LEA-501). The list never names a key that does nothing.
 */
const GO_BEYOND: Record<"agent" | "rep", ShortcutEntry> = {
  agent: {
    group: "Move and find",
    keys: "G then C / A / S",
    does: "Go to Campaigns, Standing answers or Settings",
  },
  rep: { group: "Move and find", keys: "G then S", does: "Go to Settings" },
};
const withGoTo = (entries: readonly ShortcutEntry[], agent: boolean) =>
  entries.flatMap((entry) =>
    entry.keys.startsWith("G then I") ? [entry, GO_BEYOND[agent ? "agent" : "rep"]] : [entry],
  );

/*
 * Without assignment there is no M. Each list is made once: the page registers its keys by identity
 * (`usePageShortcuts`), and a list made on every render registered again on every render.
 */
const withoutAssign = (entries: readonly ShortcutEntry[]) =>
  entries.filter((entry) => entry.keys !== "M");
const LISTS = {
  agent: withGoTo(MAIL_SHORTCUTS, true),
  agentUnassigned: withGoTo(withoutAssign(MAIL_SHORTCUTS), true),
  rep: withGoTo(MAIL_SHORTCUTS, false),
  repUnassigned: withGoTo(withoutAssign(MAIL_SHORTCUTS), false),
};

/**
 * Inbox Send has the same shortcut with or without the agent. M appears only with assignment, and
 * G then C or A only with the agent. The page's `?` panel shows these on desktop, and Settings lists
 * them.
 */
export function inboxShortcuts(agent: boolean, assignment = false): readonly ShortcutEntry[] {
  if (agent) return assignment ? LISTS.agent : LISTS.agentUnassigned;
  return assignment ? LISTS.rep : LISTS.repUnassigned;
}
